import { apiClient } from '../apiClient';
import { 
  SiteTask, 
  CreateSiteTaskRequest, 
  UpdateSiteTaskRequest,
  TaskCompletion,
  CompleteTaskRequest
} from './types';

/**
 * API implementation for site task management
 */
export class SiteTasksApiService {
  private inflightRequests = new Map<string, Promise<any>>();

  private deduplicatedGet<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
    const existing = this.inflightRequests.get(key);
    if (existing) {
      return existing as Promise<T>;
    }
    const promise = fetcher().finally(() => {
      this.inflightRequests.delete(key);
    });
    this.inflightRequests.set(key, promise);
    return promise;
  }

  /**
   * Get all tasks for a site from the API
   */
  async getSiteTasks(siteId: string): Promise<SiteTask[]> {
    return this.deduplicatedGet(`getSiteTasks:${siteId}`, async () => {
      const response = await apiClient.get<SiteTask[]>(`/sites/${siteId}/tasks`);

      if (response.success && response.data) {
        return response.data;
      }

      throw new Error('Failed to get site tasks from API');
    });
  }

  /**
   * Get site task by ID from the API
   */
  async getSiteTaskById(siteId: string, taskId: string): Promise<SiteTask> {
    const response = await apiClient.get<SiteTask>(`/sites/${siteId}/tasks/${taskId}`);
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error('Failed to get site task by ID from API');
  }

  /**
   * Create a new site task via the API
   */
  async createSiteTask(siteId: string, taskData: CreateSiteTaskRequest): Promise<SiteTask> {
    // Verbose logs commented out
    // console.log('SiteTasksApiService.createSiteTask called');
    
    const response = await apiClient.post<SiteTask>(`/sites/${siteId}/tasks`, taskData);
    
    if (response.success && response.data) {
      // Success - silent
      return response.data;
    }
    
    throw new Error('Failed to create site task via API');
  }

  /**
   * Update a site task via the API
   */
  async updateSiteTask(siteId: string, taskId: string, taskData: UpdateSiteTaskRequest): Promise<boolean> {
    // First, get the current task to ensure we have all required fields
    const currentTask = await this.getSiteTaskById(siteId, taskId);
    
    // Create a complete UpdateSiteTaskDto with all required fields
    const updateSiteTaskDto = {
      taskLocation: taskData.taskLocation || currentTask.taskLocation,
      qrCode: taskData.qrCode !== undefined ? taskData.qrCode : currentTask.qrCode || '',
      instructions: taskData.instructions || currentTask.instructions,
      frequency: taskData.frequency || currentTask.frequency,
      intervalHours: taskData.intervalHours !== undefined ? taskData.intervalHours : currentTask.intervalHours,
      orderNumber: taskData.orderNumber !== undefined ? taskData.orderNumber : currentTask.orderNumber,
      photoRequirement: taskData.photoRequirement !== undefined ? taskData.photoRequirement : currentTask.photoRequirement
    };
    
    console.log('Updating task with data:', updateSiteTaskDto);
    
    const response = await apiClient.put<void>(`/sites/${siteId}/tasks/${taskId}`, updateSiteTaskDto);
    
    if (response.success) {
      return true;
    }
    
    throw new Error('Failed to update site task via API');
  }

  /**
   * Delete a site task via the API
   */
  async deleteSiteTask(siteId: string, taskId: string): Promise<boolean> {
    const response = await apiClient.delete<void>(`/sites/${siteId}/tasks/${taskId}`);
    
    if (response.success) {
      return true;
    }
    
    throw new Error('Failed to delete site task via API');
  }

  /**
   * Complete a site task via the API
   */
  async completeTask(siteId: string, taskId: string, completionData: CompleteTaskRequest): Promise<TaskCompletion> {
    const response = await apiClient.post<TaskCompletion>(`/sites/${siteId}/tasks/${taskId}/complete`, completionData);
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error('Failed to complete task via API');
  }

  /**
   * Get all completions for all tasks at a site in a single request
   */
  async getAllSiteCompletions(siteId: string): Promise<Record<string, TaskCompletion[]>> {
    return this.deduplicatedGet(`getAllSiteCompletions:${siteId}`, async () => {
      const response = await apiClient.get<Record<string, TaskCompletion[]>>(`/sites/${siteId}/tasks/completions`);

      if (response.success && response.data) {
        return response.data;
      }

      throw new Error('Failed to get all site completions from API');
    });
  }

  /**
   * Get all completions for a task from the API
   */
  async getTaskCompletions(siteId: string, taskId: string): Promise<TaskCompletion[]> {
    return this.deduplicatedGet(`getTaskCompletions:${siteId}:${taskId}`, async () => {
      const response = await apiClient.get<TaskCompletion[]>(`/sites/${siteId}/tasks/${taskId}/completions`);

      if (response.success && response.data) {
        return response.data;
      }

      throw new Error('Failed to get task completions from API');
    });
  }

  /**
   * Reset completions for a specific task via the API
   */
  async resetTaskCompletions(siteId: string, taskId: string): Promise<boolean> {
    const response = await apiClient.post<void>(`/sites/${siteId}/tasks/${taskId}/reset-completions`, {});
    
    if (response.success) {
      return true;
    }
    
    throw new Error('Failed to reset task completions via API');
  }
  
  /**
   * Reset all completions for a site via the API
   */
  async resetAllTaskCompletions(siteId: string): Promise<boolean> {
    const response = await apiClient.post<void>(`/sites/${siteId}/tasks/reset-all-completions`, {});
    
    if (response.success) {
      return true;
    }
    
    throw new Error('Failed to reset all task completions via API');
  }
  
  /**
   * Clear all task completions (not implemented in API)
   */
  async clearAllTaskCompletions(): Promise<boolean> {
    // This is a mock-only method, not implemented in the API
    throw new Error('clearAllTaskCompletions is not implemented in the API');
  }
}

// Create a default instance
export const siteTasksApiService = new SiteTasksApiService();

export default siteTasksApiService;
